/**
 * Runtime theme bridge.
 *
 * Tailwind utility classes (`text-error-text`, `bg-success`) already resolve to
 * the CSS design tokens, so class-based color is themable for free. Color set
 * *imperatively* — ECharts series, inline `style={{ color }}`, canvas — cannot
 * read a CSS class, so it historically hardcoded hexes and ignored the theme.
 *
 * This module lets that imperative code read the same tokens at runtime, making
 * `globals.css` the single source of truth for every color. Values are read from
 * the document root via `getComputedStyle`, cached, and the cache is invalidated
 * when the active theme changes (a `[data-theme]` flip), so a future runtime
 * theme switcher re-renders charts with no per-component change.
 */

const listeners = new Set<() => void>();
let version = 0;

/**
 * Resolve a single CSS custom property (e.g. `--chart-1`) from the document root.
 * Read fresh each call (no cache) so an HMR swap of globals.css or a runtime
 * `[data-theme]` flip is reflected immediately; `getComputedStyle` is cheap at
 * the call frequency here (chart option builders, not hot render paths).
 */
export function token(name: string, fallback = '#000000'): string {
  if (typeof document === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

/** Bump the version + notify subscribers — call when the theme changes. */
export function invalidateTheme(): void {
  version += 1;
  listeners.forEach((l) => l());
}

export function subscribeTheme(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getThemeVersion(): number {
  return version;
}

// A `[data-theme]` flip (or an edit to the inline token block) re-themes the app;
// observe the root so imperative consumers refresh without a manual call.
if (typeof document !== 'undefined' && typeof MutationObserver !== 'undefined') {
  const observer = new MutationObserver(invalidateTheme);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme', 'class', 'style'],
  });
}

/** The 10-hue categorical chart series, in order (`--chart-1…10`). */
export function chartSeries(): string[] {
  return Array.from({ length: 10 }, (_, i) => token(`--chart-${i + 1}`));
}

/** A category's color by canonical key (`--category-<key>`), falling back to neutral. */
export function categoryColor(key: string): string {
  return token(`--category-${key}`, token('--category-outros', '#A8ABA8'));
}

/**
 * The semantic/role palette resolved to live hexes. Snapshot it inside a render
 * (or `useThemeColors`) so the values track the active theme.
 */
export function themePalette() {
  return {
    primary: token('--primary', '#0E5A4A'),
    primaryHover: token('--primary-hover', '#0A4A3D'),
    success: token('--success', '#298D5D'),
    warning: token('--warning', '#A06D14'),
    error: token('--error', '#BD534A'),
    info: token('--info', '#487DAE'),
    amountPositive: token('--amount-positive', '#298D5D'),
    amountNegative: token('--amount-negative', '#BD534A'),
    amountNeutral: token('--amount-neutral', '#797D7A'),
    textPrimary: token('--text-primary', '#18181B'),
    textSecondary: token('--text-secondary', '#646764'),
    textTertiary: token('--text-tertiary', '#797D7A'),
    border: token('--border', '#ECEDEC'),
    borderStrong: token('--border-strong', '#E0E2E0'),
    surface: token('--surface', '#FFFFFF'),
    surfaceSunken: token('--surface-sunken', '#F6F7F6'),
    surfaceInverse: token('--surface-inverse', '#2B2D2B'),
  } as const;
}

export type ThemePalette = ReturnType<typeof themePalette>;
